import React, { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { EmailThread, replyEmailThread } from '../../api/emailThreads';

interface Props {
  thread: EmailThread | null;
  onClose: () => void;
  onSent?: () => void;
}

function buildReplySubject(original: string): string {
  const trimmed = (original || '').trim();
  if (/^(re:|aw:)/i.test(trimmed)) return trimmed;
  return `Re: ${trimmed}`;
}

function buildQuote(t: EmailThread): string {
  const date = new Date(t.occurred_at).toLocaleString('de-DE');
  const header = `\n\nAm ${date} schrieb ${t.from_email}:\n`;
  const quoted = (t.body_text || '').split('\n').map(l => `> ${l}`).join('\n');
  return `${header}${quoted}`;
}

/** Antwort-Dialog auf eine eingehende oder ausgehende E-Mail. */
export function ReplyDialog({ thread, onClose, onSent }: Props) {
  const [to, setTo]             = useState('');
  const [subject, setSubject]   = useState('');
  const [body, setBody]         = useState('');
  const [sending, setSending]   = useState(false);
  const [error, setError]       = useState<string | null>(null);

  useEffect(() => {
    if (!thread) return;
    // Empfänger: bei eingehenden Mails antworten wir an from_email,
    // bei ausgehenden an die ursprüngliche to_email (Folge-Mail).
    const replyTo = thread.direction === 'received' ? thread.from_email : thread.to_email;
    setTo(replyTo || '');
    setSubject(buildReplySubject(thread.subject));
    setBody(buildQuote(thread));
    setError(null);
  }, [thread]);

  const handleSend = async () => {
    if (!thread || !to || !subject || !body) return;
    setSending(true);
    setError(null);
    try {
      await replyEmailThread({
        contact_id:  thread.contact_id ?? undefined,
        mandate_id:  thread.mandate_id ?? undefined,
        to_email:    to,
        subject,
        body_text:   body,
        in_reply_to: thread.message_id ?? undefined,
      });
      onSent?.();
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      setError(msg ?? (err instanceof Error ? err.message : 'Fehler beim Versenden'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen={!!thread} onClose={onClose} title="Antworten" size="lg">
      {thread && (
        <div style={{ display: 'grid', gap: '12px' }}>
          <div style={{ padding: '10px 12px', background: 'var(--surface2)', borderRadius: 'var(--radius)', fontSize: '12px', color: 'var(--text3)' }}>
            Antwort auf: <strong style={{ color: 'var(--text2)' }}>{thread.subject}</strong>
            <br />
            Vom {new Date(thread.occurred_at).toLocaleString('de-DE')}
          </div>

          <div>
            <label style={labelStyle}>An</label>
            <input type="email" value={to} onChange={e => setTo(e.target.value)}
              style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Betreff</label>
            <input value={subject} onChange={e => setSubject(e.target.value)}
              style={inputStyle} />
          </div>

          <div>
            <label style={labelStyle}>Text</label>
            <textarea value={body} onChange={e => setBody(e.target.value)}
              rows={14}
              style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }} />
          </div>

          {error && (
            <div style={{ padding: '10px 14px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 'var(--radius)', fontSize: '13px' }}>
              ⚠️ {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '6px' }}>
            <button onClick={onClose} disabled={sending}
              style={{ padding: '8px 16px', background: 'var(--surface)', border: '1px solid var(--border2)',
                borderRadius: 'var(--radius)', fontSize: '13px', cursor: sending ? 'not-allowed' : 'pointer', color: 'var(--text2)' }}>
              Abbrechen
            </button>
            <button onClick={handleSend} disabled={sending || !to || !subject || !body}
              style={{
                padding: '9px 22px', fontSize: '13px', fontWeight: 600,
                background: 'var(--accent)', color: '#fff', border: 'none',
                borderRadius: 'var(--radius)', cursor: sending ? 'wait' : 'pointer',
                opacity: sending ? 0.7 : 1,
              }}>
              {sending ? '⏳ Wird versendet …' : '✉ Antwort senden'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '11px', fontWeight: 500,
  textTransform: 'uppercase', letterSpacing: '0.5px',
  color: 'var(--text3)', marginBottom: '5px',
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: '13px',
  border: '1px solid var(--border2)', borderRadius: 'var(--radius)',
  background: 'var(--surface)', color: 'var(--text)',
};
